import { useEffect, useState } from "react";
import {
  Banner,
  BlockStack,
  Button,
  Checkbox,
  InlineStack,
  Text,
  TextField,
} from "@shopify/polaris";
import { publicationRequest } from "../../../../packages/shared/stock-publication/client";
import type { QueueAccount } from "../../../../packages/shared/shopify-queue/client";
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
type Page = {
  products: {
    id: number;
    sku: string | null;
    title: string;
    variant_gid: string | null;
  }[];
  nextCursor: number | null;
};
export function ProductSelection({
  account,
  connectionId,
  selected,
  onChange,
  disabled,
}: {
  account: QueueAccount;
  connectionId: string;
  selected: number[];
  onChange: (ids: number[]) => void;
  disabled: boolean;
}) {
  const [query, setQuery] = useState(""),
    [pages, setPages] = useState<number[]>([]),
    [data, setData] = useState<Page | null>(null),
    [error, setError] = useState(""),
    [refresh, setRefresh] = useState(0);
  const cursor = pages[pages.length - 1];
  useEffect(() => {
    const abort = new AbortController();
    setData(null);
    setError("");
    void publicationRequest<Page>(
      base,
      account,
      `/products?connection_id=${encodeURIComponent(connectionId)}&q=${encodeURIComponent(query)}${cursor ? "&cursor=" + cursor : ""}`,
      { signal: abort.signal },
    )
      .then((value) => {
        if (!abort.signal.aborted) setData(value);
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [account, connectionId, query, cursor, refresh]);
  return (
    <BlockStack gap="200">
      <TextField
        label="Find mapped products"
        autoComplete="off"
        value={query}
        onChange={(value) => {
          setQuery(value);
          setPages([]);
        }}
        disabled={disabled}
      />
      <Text as="p">
        {selected.length} selected. Select up to 500 products, or use all mapped
        products.
      </Text>
      {error && <Banner tone="critical">{error}</Banner>}
      {!data && !error && <Text as="p">Loading products…</Text>}
      {data && !data.products.length && (
        <Text as="p">No mapped products match this search.</Text>
      )}
      {data?.products.map((row) => (
        <Checkbox
          key={row.id}
          label={`${row.sku || "#" + row.id} · ${row.title}`}
          checked={selected.includes(row.id)}
          disabled={
            disabled || (!selected.includes(row.id) && selected.length >= 500)
          }
          onChange={(checked) =>
            onChange(
              checked
                ? [...selected, row.id]
                : selected.filter((id) => id !== row.id),
            )
          }
        />
      ))}
      <InlineStack gap="200">
        <Button
          disabled={disabled}
          onClick={() => {
            setPages([]);
            setRefresh((v) => v + 1);
          }}
        >
          Refresh products
        </Button>
        {!!selected.length && (
          <Button disabled={disabled} onClick={() => onChange([])}>
            Clear selection
          </Button>
        )}
        {pages.length > 0 && (
          <Button
            disabled={disabled}
            onClick={() => setPages((p) => p.slice(0, -1))}
          >
            Previous products
          </Button>
        )}
        {data?.nextCursor && (
          <Button
            disabled={disabled}
            onClick={() => setPages((p) => [...p, data.nextCursor!])}
          >
            More products
          </Button>
        )}
      </InlineStack>
    </BlockStack>
  );
}
